/**
 * İSTEMCİ TARAFI META YÖNETİMİ
 *
 * Sunucu (server.ts) ilk HTML'e doğru başlığı/OG etiketlerini yazar —
 * paylaşım ve arama motoru bunu görür. Bu modül ise SPA gezinmesi sırasında
 * başlığı ve canonical'ı günceller, böylece tarayıcı sekmesi ve geçmiş
 * doğru kalır.
 *
 * İki katman aynı veriden beslenir; farklı metin üretmezler.
 */

interface PageMeta {
  title: string;
  description?: string | null;
  canonicalPath?: string;
  imageUrl?: string | null;
  /** Filtreli liste sayfaları dizine eklenmez. */
  noindex?: boolean;
  type?: 'website' | 'product';
}

function upsertMeta(selector: string, attributes: Record<string, string>): void {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value);
  }
}

function upsertLink(rel: string, href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }

  element.href = href;
}

export function applyPageMeta(meta: PageMeta): void {
  document.title = meta.title;

  if (meta.description) {
    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: meta.description,
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: meta.description,
    });
  }

  upsertMeta('meta[property="og:title"]', {
    property: 'og:title',
    content: meta.title,
  });

  upsertMeta('meta[property="og:type"]', {
    property: 'og:type',
    content: meta.type ?? 'website',
  });

  if (meta.imageUrl) {
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: meta.imageUrl,
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
  }

  if (meta.canonicalPath) {
    const canonical = `${window.location.origin}${meta.canonicalPath}`;
    upsertLink('canonical', canonical);
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonical,
    });
  }

  // Filtre kombinasyonları sonsuz sayıda URL üretir; bunlar dizine eklenmez.
  upsertMeta('meta[name="robots"]', {
    name: 'robots',
    content: meta.noindex ? 'noindex, follow' : 'index, follow',
  });
}

const JSON_LD_ID = 'page-structured-data';

/** Ürün detayında gerçek veriden yapısal veri üretir. */
export function applyStructuredData(data: object | null): void {
  const existing = document.getElementById(JSON_LD_ID);

  if (existing) {
    existing.remove();
  }

  if (!data) {
    return;
  }

  const script = document.createElement('script');
  script.id = JSON_LD_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * TESLİMAT BİLGİSİ (schema.org)
 *
 * Google, arama sonucunda ürünün altına teslimat ücretini ve süresini
 * yazabilir. Buradaki değerlerin tamamı teslimat motorundan gelir —
 * "ücretsiz kargo" gibi tutulamayacak bir söz ASLA uydurulmaz.
 */
export interface ProductShipping {
  /** Seçili bölgenin gerçek teslimat ücreti ("60.00"). */
  feeAmount: string;
  /** Bu tutarın üzerinde teslimat ücretsiz; yoksa null. */
  freeThreshold: string | null;
  /** Hizmet verilen il — "Isparta". */
  region: string;
  /** Bu ürün bugün gönderilebiliyor mu? Hazırlık süresini belirler. */
  sameDay: boolean;
}

function buildShippingDetails(
  shipping: ProductShipping,
  price: string,
): Record<string, unknown> {
  /**
   * Ürünün kendi fiyatı ücretsiz teslimat sınırını aşıyorsa o ürün için
   * teslimat gerçekten ücretsizdir; şemaya 0 yazmak doğru olur.
   */
  const free =
    shipping.freeThreshold !== null &&
    Number(price) >= Number(shipping.freeThreshold);

  return {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: free ? '0' : shipping.feeAmount,
      currency: 'TRY',
    },
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: 'TR',
      addressRegion: shipping.region,
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      // Aynı gün gönderilebilen üründe hazırlık aynı gün başlar (0 gün).
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: shipping.sameDay ? 0 : 1,
        maxValue: 1,
        unitCode: 'DAY',
      },
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: 0,
        maxValue: 1,
        unitCode: 'DAY',
      },
    },
  };
}

export function buildProductJsonLd(params: {
  name: string;
  description: string | null;
  imageUrl: string | null;
  price: string;
  sku: string | null;
  inStock: boolean;
  url: string;
  rating?: { average: number; count: number } | null;
  /** Ürünleri mağaza kendi hazırlar; marka mağazanın kendisidir. */
  brand?: string | null;
  shipping?: ProductShipping | null;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: params.name,
    ...(params.description ? { description: params.description } : {}),
    ...(params.imageUrl ? { image: [params.imageUrl] } : {}),
    ...(params.sku ? { sku: params.sku } : {}),
    ...(params.brand ? { brand: { '@type': 'Brand', name: params.brand } } : {}),
    offers: {
      '@type': 'Offer',
      priceCurrency: 'TRY',
      price: params.price,
      availability: params.inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      url: params.url,
      ...(params.shipping
        ? { shippingDetails: buildShippingDetails(params.shipping, params.price) }
        : {}),
      /**
       * İADE POLİTİKASI
       *
       * Kesme çiçek çabuk bozulan bir maldır; mesafeli satış mevzuatı bu
       * ürünleri cayma hakkının istisnaları arasında sayar. Hatalı ya da
       * hasarlı teslimatta ürün yenilenir — bu bir iade değil, ayıplı mal
       * yükümlülüğüdür ve şemada iade politikası olarak beyan edilmez.
       */
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'TR',
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
      },
    },
    ...(params.rating && params.rating.count > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: params.rating.average,
            reviewCount: params.rating.count,
          },
        }
      : {}),
  };
}

export function buildBreadcrumbJsonLd(
  items: Array<{ name: string; path: string }>,
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${window.location.origin}${item.path}`,
    })),
  };
}

/**
 * SSS yapısal verisi.
 *
 * YALNIZCA sayfada GERÇEKTEN görünen sorular için üretilir. Şemada olup
 * ekranda olmayan soru-cevap, Google'ın yapısal veri kurallarını ihlal eder
 * ve elle işlem (manual action) sebebidir.
 */
export function buildFaqJsonLd(
  items: Array<{ question: string; answer: string }>,
): object | null {
  if (items.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}
